<!-- eslint-disable no-invalid-this -->
<template>
  <single-function-layout @close="goToOrders" class="export-orders" :loading="loading">
    <fade-transition :mode="'out-in'">
      <div v-if="!completedExport" key="1">
        <div class="header">
          <h1>{{ $t('exportOrders.title') }}</h1>
          <p>{{ $t('exportOrders.description') }}</p>
        </div>
        <div class="body">
          <div class="radio-buttons">
            <sb-radio-button
              v-model="selectedExportType"
              :value="currentSelectionModel"
              class="radio-button-wrapper"
            >
              <span class="label">Current Selection</span>
              <p class="label-subtext">{{ numberOfSelectedOrderIds }} orders</p>
            </sb-radio-button>

            <sb-radio-button
              v-model="selectedExportType"
              :value="currentPageModel"
              class="radio-button-wrapper"
            >
              <span class="label">Current Page</span>
              <p class="label-subtext">{{ numberOfCurrentPageIds }} orders</p>
            </sb-radio-button>

            <sb-radio-button
              v-model="selectedExportType"
              :value="allOrdersWithCurrentFiltersModel"
              class="radio-button-wrapper"
            >
              <span class="label">All orders with Current Filters</span>
            </sb-radio-button>
          </div>
        </div>
        <div class="footer">
          <a href="#" @click.prevent="goToOrders">{{ $t('exportOrders.actions.cancel') }}</a>
          <button class="btn btn-primary" @click.prevent="exportOrders">{{ $t('exportOrders.actions.exportOrders') }}</button>
        </div>
      </div>
      <div v-else key="2">
        <div class="header">
          <span class="icon icon-mail"></span>
          <h1 class="confirm-title">{{ $t('exportOrders.titleConfirm') }}</h1>
          <p>{{ $t('exportOrders.descriptionConfirm') }}</p>
          <a v-if="!isPoweredBySubAccountUser" :href="`mailto:${$t('exportOrders.supportEmail')}`">
            {{$t('exportOrders.supportEmail')}}
          </a>
        </div>
        <div class="footer">
          <a href="#" @click.prevent="goToOrders">{{ $t('exportOrders.actions.close') }}</a>
        </div>
        <div class="alertMessage">
          <SbBanner variation="warning">
            <p>{{ $t('exportOrders.alertMessage') }}</p>
          </SbBanner>
        </div>
      </div>
    </fade-transition>
  </single-function-layout>
</template>

<script>
import SingleFunctionLayout from '@/components/layouts/SingleFunction'
import { ExportSelections } from '@enums/modules/orders/exportTypes'
import { OrdersEnum } from '@enums/modules/routes'
import { promisePipe } from '@utils/functionalBob'
import { debounce } from 'lodash'

export default {
  name: 'ExportOrders',
  components: {
    'single-function-layout': SingleFunctionLayout
  },
  inject: ['isPoweredBySubAccountUser'],
  data: () => ({
    selectedExportType: ExportSelections.CurrentSelection,
    completedExport: false,
    loading: false
  }),
  mounted () {
    this.selectedExportType = this.allOrdersSelected ? ExportSelections.AllOrders : ExportSelections.CurrentSelection
  },
  computed: {
    allOrdersSelected () {
      return this.$shipBobStore.getters['orders/allOrdersSelected']
    },
    selectedOrderIds () {
      return this.$shipBobStore.getters['orders/flattenedSelectedOrderIds']
    },
    currentPageIds () {
      return this.$shipBobStore.getters['orders/orderIdsInActiveView']
    },
    numberOfSelectedOrderIds () {
      if (this.allOrdersSelected) return this.$shipBobStore.getters['orders/pagination'].totalCount
      return this.selectedOrderIds.length
    },
    numberOfCurrentPageIds () {
      return this.currentPageIds.length
    },
    currentSelectionModel () {
      return this.allOrdersSelected ? ExportSelections.AllOrders : ExportSelections.CurrentSelection
    },
    currentPageModel () {
      return ExportSelections.CurrentPage
    },
    allOrdersWithCurrentFiltersModel () {
      return ExportSelections.CurrentFilters
    }
  },
  methods: {
    exportOrders: debounce(async function () {
      // eslint-disable-next-line no-invalid-this
      const vm = this
      const toggleLoader = function (on) {
        return function (val) {
          vm.loading = on
          return val
        }
      }
      if (!vm.selectedExportType) return
      const exportOrders = promisePipe(
        toggleLoader(true),
        async (selectedExportType) => await vm.$shipBobStore.dispatch('orders/exportOrders', selectedExportType),
        toggleLoader(false)
      )
      const success = await exportOrders(vm.selectedExportType)
      if (success) {
        setTimeout(() => {
          vm.completedExport = true
          vm.$shipBobStore.dispatch('orders/clearSelectedOrders')
        }, 1000)
      }
    }, 500),
    goToOrders () {
      this.$router.push({ name: OrdersEnum.Orders })
    }
  },
  i18n: {
    messages: {
      'en-US': {
        exportOrders: {
          title: 'Exporting your orders',
          titleConfirm: 'Emailing you your export',
          description: 'The export can take some time to prepare. We will email you with a link to download the report of your orders.',
          descriptionConfirm: 'We are preparing your data and you will receive an email with a link within 15 minutes. Please do not re-submit your request within the 15 minute waiting period. If you experience longer waiting times, please reach out to:',
          supportEmail: 'support@shipbob.com',
          alertMessage: 'The email will be sent to your email address. Please ensure to check if that has gone to your spam folders if you don\'t see it!',
          selections: {
            currentSelection: 'Current Selection',
            subText: 'orders',
            currentPage: 'Current Page',
            allOrdersWithFilters: 'All orders with Current Filters'
          },
          actions: {
            cancel: 'Cancel',
            exportOrders: 'Export Orders',
            close: 'Close'
          }
        }
      }
    }
  }
}
</script>

<style scoped lang='scss'>
@import '../../../assets/scss/shipbob-custom/components/orders/_orderAction.scss';
.radio-buttons {
  display: flex;
  flex-direction: column;
}
.radio-button-wrapper {
  color: #343e4f;
  margin-left: 0.5em;
  .label {
    font-weight: 600;
    line-height: 1em;
  }
  .label-subtext {
    font-size: 0.8em;
    line-height: 1.5em;
    color: #9ea1a7;
  }
}
</style>
